import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Buildings, ArrowLeft, User as UserIcon, Phone, MapPin, Envelope, FileText, HardDrives, Ticket as TicketIcon, FilePdf } from "@phosphor-icons/react";

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const r = await api.get(`/companies/${id}`); setCompany(r.data); }
      catch { toast.error("Société introuvable"); nav("/societes"); }
      finally { setLoading(false); }
    })();
  }, [id, nav]);

  if (loading) return <div className="text-muted-foreground">Chargement…</div>;
  if (!company) return null;

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={() => nav("/societes")} className="-ml-2">
        <ArrowLeft size={14} className="mr-1.5"/> Retour aux sociétés
      </Button>

      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
            <Buildings size={28} weight="fill" className="text-primary"/>
          </div>
          <div>
            <div className="label-caps">Société cliente</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{company.name}</h1>
            <div className="flex flex-wrap gap-1 mt-2">
              {(company.email_domains || []).map((d) => (
                <span key={d} className="mono text-xs px-2 py-0.5 rounded bg-muted border">{d}</span>
              ))}
            </div>
          </div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          data-testid="download-company-inventory-pdf"
          onClick={async () => {
            try {
              const r = await api.get(`/companies/${id}/inventory.pdf`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              window.open(url, "_blank", "noopener");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            } catch { toast.error("Erreur lors de la génération du PDF"); }
          }}
        >
          <FilePdf size={14} className="mr-1.5"/> Inventaire PDF
        </Button>
      </header>

      <Card><CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          <InfoTile icon={Phone} label="Téléphone" value={company.main_phone || "—"}/>
          <InfoTile icon={MapPin} label="Adresse" value={company.address || "—"}/>
          <InfoTile icon={MapPin} label="Ville" value={[company.postal_code, company.city].filter(Boolean).join(" ") || "—"}/>
          <InfoTile icon={MapPin} label="Pays" value={company.country || "—"}/>
        </div>
        {company.notes && (
          <div className="mt-4 pt-4 border-t">
            <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-1.5">Notes</div>
            <p className="text-sm whitespace-pre-wrap">{company.notes}</p>
          </div>
        )}
      </CardContent></Card>

      {/* Contacts */}
      <Section title={`Contacts (${company.contacts.length})`} icon={UserIcon}>
        {company.contacts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun contact rattaché. Les futurs emails provenant des domaines ci-dessus créeront automatiquement des contacts ici.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {company.contacts.map((u) => (
              <div key={u.id} className="p-3 rounded-lg border bg-background hover:bg-muted/40" data-testid={`company-contact-${u.id}`}>
                <div className="font-medium">{u.full_name}</div>
                <a href={`mailto:${u.email}`} className="text-xs text-primary hover:underline mono">{u.email}</a>
                {u.phone && <div className="text-xs text-muted-foreground mt-0.5"><Phone size={11} className="inline mr-1"/>{u.phone}</div>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Contracts */}
      <Section title={`Contrats (${company.contracts.length})`} icon={FileText}>
        {company.contracts.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun contrat lié à cette société.</div>
        ) : (
          <div className="space-y-3">
            {company.contracts.map((c) => {
              const pct = c.month_percent || 0;
              const overrun = c.overrun_this_month || 0;
              const barColor = overrun > 0 ? "bg-red-500" : pct > 80 ? "bg-orange-500" : "bg-emerald-500";
              return (
                <div key={c.id} className="p-3 rounded-lg border" data-testid={`company-contract-${c.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {c.monthly_hours}h/mois · {c.hourly_rate}€/h
                        {(c.services || []).length > 0 && ` · ${c.services.length} service(s)`}
                      </div>
                    </div>
                    <Badge variant={c.active ? "default" : "outline"}>{c.active ? "Actif" : "Inactif"}</Badge>
                  </div>
                  {c.active && c.monthly_hours > 0 && (
                    <>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-muted-foreground">Mois en cours</span>
                        <span className={overrun > 0 ? "text-red-600 font-semibold" : "font-medium"}>
                          {c.hours_this_month}h / {c.monthly_hours}h {overrun > 0 && `(+${overrun}h dépassement)`}
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(100, pct)}%` }}/>
                      </div>
                      <div className="text-[0.7rem] text-muted-foreground mt-1.5 flex justify-between">
                        <span>Reste : <b>{c.hours_remaining}h</b> ce mois</span>
                        <span>Année : <b>{c.hours_this_year}h</b> consommées</span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Equipment */}
      <Section title={`Équipements (${company.equipment.length})`} icon={HardDrives}>
        {company.equipment.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun équipement enregistré.</div>
        ) : (
          <div className="text-sm">{company.equipment.length} appareil(s) — voir <Link to="/inventaire" className="text-primary hover:underline">l'inventaire</Link> filtré par cette société.</div>
        )}
      </Section>

      {/* Recent tickets */}
      <Section title={`Tickets récents (${company.tickets.length})`} icon={TicketIcon}>
        {company.tickets.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">Aucun ticket enregistré pour les contacts de cette société.</div>
        ) : (
          <div className="space-y-1">
            {company.tickets.map((t) => (
              <Link key={t.id} to={`/tickets/${t.id}`} className="flex items-center justify-between p-2 rounded hover:bg-muted/40" data-testid={`company-ticket-${t.number}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="mono text-xs text-muted-foreground">#{t.number}</span>
                  <span className="truncate">{t.subject}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0 text-xs">
                  <Badge variant="outline" className="uppercase">{t.status}</Badge>
                  <span className="text-muted-foreground">{t.client_name}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card><CardContent className="pt-6">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={18} className="text-primary"/>}
        <h2 className="font-display font-semibold text-lg">{title}</h2>
      </div>
      {children}
    </CardContent></Card>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="text-[0.7rem] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={12}/>} {label}
      </div>
      <div className="font-medium truncate">{value}</div>
    </div>
  );
}
